import { omit, isEmpty, toLower } from 'lodash';
import { axios } from './common';
import { CM_BASE_URL } from '../../pages/constants/cmConstants';
import { convertLocalEpochToUTC } from '../../util/timezone';

export const fetchAlerts = async ({ filters, currentPage, pageSize }) => {
  const params = {
    level: filters.alertLevel,
    typologyIds: !isEmpty(filters.typologyIds)
      ? filters.typologyIds.join(',')
      : null,
    sources: !isEmpty(filters.sourceIds) ? filters.sourceIds.join(',') : null,
    ...omit(filters, [
      'alertLevel',
      'searchField',
      'searchText',
      'typologyIds',
      'sourceIds'
    ]),
    offset: (currentPage - 1) * pageSize,
    limit: pageSize,
    from: convertLocalEpochToUTC(filters.from),
    to: convertLocalEpochToUTC(filters.to)
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/alerts`;

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error('Failed to fetch alerts');
    }
    return res.data;
  });
};

export const fetchAlertDetailsById = async (alertId) => {
  const url = `${CM_BASE_URL}/alerts/byId/${alertId}`;
  const res = await axios.get(url);

  if (res.status !== 200 && res.status !== 404) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const fetchWhitelistByCustomer = async (alertId) => {
  const url = `${CM_BASE_URL}/alerts/${alertId}/whitelists`;
  const res = await axios.get(url);

  if (res.status !== 200 && res.status !== 404) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const updateWhitelistByCustomer = async (payload) => {
  const { startDate, endDate, notes, alertId } = payload;

  const requestBody = {
    startDate,
    endDate,
    notes
  };
  const url = `${CM_BASE_URL}/alerts/${alertId}/whitelists`;
  const res = await axios.put(url, requestBody);

  if (res.status !== 200 && res.status !== 404 && res.status !== 202) {
    throw new Error(res.data.error ?? 'Error fetching alert details');
  } else if (res.status === 404) {
    throw new Error("You don't have access to this alert");
  }

  return res.data;
};

export const changeAlertStatus = async ({ alertId, status }) => {
  const url = `${CM_BASE_URL}/alerts/${alertId}/status/${status}`;

  return axios.put(url).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error(res.data?.msg ?? 'Failed to change alert status');
    }
    return res.data;
  });
};

export const assignAlertsToUser = async ({ alertIds, userId }) => {
  const requestBody = {
    alertIds,
    user: userId
  };

  const url = `${CM_BASE_URL}/alerts/assignToUser`;

  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error(
        res.data?.msg ??
          'Something went wrong. Please try again after sometime.'
      );
    }
    return res.data;
  });
};

export const addAlertNote = async ({ alertId, alertNote }) => {
  const requestBody = {
    alertId,
    note: alertNote
  };

  const url = `${CM_BASE_URL}/history/addNote`;

  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: 'Failed to add alert note' };
    }
    return res.data;
  });
};

export const createCaseFromAlerts = async ({ alertIds, departmentId }) => {
  const requestBody = {
    alertIds,
    departmentId
  };
  const url = `${CM_BASE_URL}/cases/fromAlerts`;
  return axios.post(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw new Error(res.data.error ?? 'Failed to convert alert(s) to a case');
    }
    return res.data;
  });
};

export const mergeAlertsToCase = async ({ alertIds, caseId }) => {
  const requestBody = {
    alertIds,
    caseId
  };
  const url = `${CM_BASE_URL}/cases/${caseId}/addAlerts`;
  return axios.put(url, requestBody).then((res) => {
    if (res.status !== 200 && res.data.status !== 'success') {
      throw { message: res.data.error };
    }
    return res.data;
  });
};

export const fetchAlertsRelayHistoryList = async ({
  filters = [],
  currentPage,
  pageSize
}) => {
  const params = {
    status: filters?.status,
    isLatest: filters?.isLatest,
    offset: (currentPage - 1) * pageSize,
    limit: pageSize
  };

  if (filters.searchField && filters.searchText) {
    params[filters.searchField] = toLower(filters.searchText);
  }

  const url = `${CM_BASE_URL}/retry-history/all`;

  return axios.get(url, { params }).then((res) => {
    if (res.status !== 200) {
      throw new Error('Failed to fetch alert retry history');
    }
    return res.data;
  });
};
